import React, { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Member } from '@/types/member';
import { MapPin, Briefcase, MessageCircle, UserPlus, Eye } from 'lucide-react';

interface EnhancedAvatarProps {
  member: Member;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: () => void;
  showHover?: boolean;
  onMessage?: (member: Member) => void;
  onConnect?: (member: Member) => void;
  className?: string;
}

const sizeClasses = {
  sm: 'h-16 w-16', // 64px
  md: 'h-20 w-20', // 80px  
  lg: 'h-24 w-24', // 96px
  xl: 'h-32 w-32'  // 128px
};

const hoverSizeClasses = {
  sm: 'h-24 w-24',
  md: 'h-28 w-28', 
  lg: 'h-32 w-32',
  xl: 'h-40 w-40'
};

export const EnhancedAvatar: React.FC<EnhancedAvatarProps> = ({
  member,
  size = 'lg',
  onClick,
  showHover = true,
  onMessage,
  onConnect,
  className
}) => {
  const [imageError, setImageError] = useState(false);
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const avatarContent = (
    <Avatar 
      className={cn(
        sizeClasses[size],
        "cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg border-2 border-border/50 hover:border-primary/50",
        className
      )}
      onClick={onClick}
    >
      {!imageError && member.image ? (
        <AvatarImage 
          src={member.image} 
          alt={member.name}
          className="object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-primary/20 to-secondary/20">
          {getInitials(member.name)}
        </AvatarFallback>
      )}
    </Avatar>
  );

  if (!showHover) {
    return avatarContent;
  }

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {avatarContent}
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-80 p-4 z-50" 
        side="top" 
        align="center"
        sideOffset={10}
      >
        <div className="space-y-4">
          {/* Large avatar with member name */}
          <div className="flex items-center gap-4">
            <Avatar className={cn(hoverSizeClasses[size], "border-2 border-border")}>
              {!imageError && member.image ? (
                <AvatarImage 
                  src={member.image} 
                  alt={member.name}
                  className="object-cover"
                />
              ) : (
                <AvatarFallback className="text-xl font-semibold bg-gradient-to-br from-primary/20 to-secondary/20">
                  {getInitials(member.name)}
                </AvatarFallback>
              )}
            </Avatar>
            
            <div className="flex-1 space-y-1">
              <h3 className="font-semibold text-foreground">{member.name}</h3>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Briefcase className="h-3 w-3" />
                  <span className="line-clamp-1">{member.role}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1">{member.company}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  <span>{member.city}, {member.country}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bio preview */}
          {member.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              {member.bio}
            </p>
          )}

          {/* Key skills */}
          {member.skills && member.skills.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">Compétences clés</h4>
              <div className="flex flex-wrap gap-1">
                {member.skills.slice(0, 4).map((skill, index) => (
                  <Badge key={index} variant="green" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {member.skills.length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    +{member.skills.length - 4}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="flex gap-2 pt-2 border-t">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
            >
              <Eye className="h-3 w-3 mr-1" />
              Voir
            </Button>
            {onMessage && (
              <Button
                size="sm"
                variant="outline" 
                className="flex-1 h-8 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage(member);
                }}
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                Message
              </Button>
            )}
            {onConnect && (
              <Button
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onConnect(member);
                }}
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Connecter
              </Button>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};